import { Bell } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { useNotifications } from "../hooks/useNotifications";
import { NotificationItem } from "./NotificationItem";
import type { Notification } from "../types";

export function NotificationBell() {
  const { notifications, unreadCount, markRead, markAllRead } = useNotifications();
  const navigate = useNavigate();

  const handleItemClick = (notification: Notification) => {
    if (!notification.read) markRead(notification.id);
    if (notification.href) navigate(notification.href);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative h-12 w-12 lg:h-10 lg:w-10"
          aria-label={
            unreadCount > 0
              ? `Notificações (${unreadCount} não lidas)`
              : "Notificações"
          }
        >
          <Bell
            weight={unreadCount > 0 ? "fill" : "light"}
            aria-hidden="true"
            className={cn(
              "w-5 h-5 transition-colors",
              unreadCount > 0 ? "text-foreground" : "text-muted-foreground",
            )}
          />
          {unreadCount > 0 && (
            <span
              className="absolute top-1.5 right-1.5 lg:top-1 lg:right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center leading-none pointer-events-none"
              aria-hidden="true"
            >
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent align="end" sideOffset={8} className="w-80 p-0 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <h3 className="text-sm font-semibold">Notificações</h3>
          {unreadCount > 0 && (
            <button
              type="button"
              onClick={markAllRead}
              className="text-xs text-primary hover:text-primary/80 transition-colors"
            >
              Marcar todas como lidas
            </button>
          )}
        </div>

        {/* List */}
        {notifications.length === 0 ? (
          <div className="py-10 text-center">
            <Bell weight="light" className="w-8 h-8 text-muted-foreground/40 mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">Nenhuma notificação ainda</p>
          </div>
        ) : (
          <ScrollArea className="max-h-[380px]">
            <div className="divide-y divide-border/50">
              {notifications.slice(0, 10).map((n) => (
                <NotificationItem key={n.id} notification={n} onClick={handleItemClick} />
              ))}
            </div>
          </ScrollArea>
        )}

        {/* Footer */}
        <div className="border-t border-border">
          <button
            type="button"
            onClick={() => navigate("/notificacoes")}
            className="w-full py-2.5 text-xs text-center text-muted-foreground hover:text-foreground transition-colors"
          >
            Ver todas as notificações
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
